import { LightingModeType, type Maybe, TransactionState } from "types";
import { AppState } from "types/mainTypes";

export type ConvergenceInput = {
	appState: AppState;
	hasTransaction: boolean;
	transactionState: Maybe<TransactionState>;
	// True from the moment a transaction reaches Finished until the customer leaves (idle) or a new
	// transaction starts — i.e. the post-order hand-off window, including after the transaction object
	// has been cleared. Computed by the converger (it needs the Finished→cleared transition history).
	inPostOrderHandoff: boolean;
	openDetailDishTypeId: string | null;
	allergens: string[];
	filteredDishTypeIds: readonly string[];
};

export type DesiredFridgeLighting =
	| { kind: "be-authoritative" }
	| { kind: "fe-driven"; mode: LightingModeType; spotlightDishTypeIds: readonly string[] };

// Precedence: Finished/post-order-handoff > (IDLE && !hasTransaction) > allergen-filter > detail-open > default Active.
export const computeDesiredLighting = (input: ConvergenceInput): DesiredFridgeLighting => {
	const {
		transactionState,
		appState,
		hasTransaction,
		inPostOrderHandoff,
		allergens,
		filteredDishTypeIds,
		openDetailDishTypeId,
	} = input;

	// During Finished AND through the post-order hand-off window, defer to the BE. Without the
	// hand-off the converger snapped back to fe-driven ACTIVE the instant the order cleared and fought
	// the BE's post-order StandBy, producing a STAND_BY↔ACTIVE command war. The FE resumes control on
	// the next transaction or once the customer leaves (idle → normal welcome flow).
	if (transactionState === TransactionState.Finished || inPostOrderHandoff) {
		return { kind: "be-authoritative" };
	}

	if (appState === AppState.IDLE && !hasTransaction) {
		return { kind: "fe-driven", mode: LightingModeType.StandBy, spotlightDishTypeIds: [] };
	}

	if (allergens.length > 0 && transactionState !== TransactionState.StockCheck) {
		if (openDetailDishTypeId !== null && filteredDishTypeIds.includes(openDetailDishTypeId)) {
			return {
				kind: "fe-driven",
				mode: LightingModeType.Active,
				spotlightDishTypeIds: [openDetailDishTypeId],
			};
		}

		return {
			kind: "fe-driven",
			mode: LightingModeType.Active,
			spotlightDishTypeIds: filteredDishTypeIds,
		};
	}

	if (openDetailDishTypeId !== null) {
		return {
			kind: "fe-driven",
			mode: LightingModeType.Active,
			spotlightDishTypeIds: [openDetailDishTypeId],
		};
	}

	return { kind: "fe-driven", mode: LightingModeType.Active, spotlightDishTypeIds: [] };
};
